let _isES9Supported;
let _isES11Supported;
let _isES13Supported;

function isES9Supported() {
  if (_isES9Supported === undefined) {
    _isES9Supported =
      exported.test(
        'return (() => { const o = { t: 1 }; return { ...o }; })() && (async () => ({}))()',
      ) && !!Object.values;
  }

  return _isES9Supported;
}

function isES11Supported() {
  if (_isES11Supported === undefined) {
    _isES11Supported =
      exported.test(
        'return (() => { const o = { t: { q: true } }; return o?.t?.q && (o?.a?.q ?? true); })()',
      ) &&
      exported.test('return typeof Promise.allSettled === "function"') &&
      exported.test('return typeof globalThis !== "undefined"') &&
      exported.test('return typeof 9007199254740991n === "bigint"');
  }

  return _isES11Supported;
}

function isES13Supported() {
  if (_isES13Supported === undefined) {
    _isES13Supported =
      exported.test('return [1,1].findLast(e => e === 1)') &&
      exported.test('return Object.hasOwn({a:1}, "a")');
  }

  return _isES13Supported;
}

function test(snippet) {
  try {
    const fn = new Function(snippet);
    const result = fn();

    return !!result;
  } catch (e) {
    return false;
  }
}

const exported = {
  isES9Supported,
  isES11Supported,
  isES13Supported,
  test,
};

function _loadScript(asset, root) {
  return new Promise((resolve, reject) => {
    const scriptElement = root.querySelector(`script[src='${asset.source}']`);

    if (scriptElement) {
      if (!asset.test || exported.test(asset.test)) {
        resolve();
      }

      scriptElement.addEventListener('load', resolve);
      scriptElement.addEventListener(
        'error',
        asset.optional ? resolve : reject,
      );
      return;
    }

    const script = document.createElement('script');

    if (asset.type === 'script') {
      script.defer = true;
      script.onload = resolve;
      script.onerror = (error) => {
        script.remove();

        asset.optional ? resolve(error) : reject(error);
      };
      script.src = asset.source;

      const { attr } = asset;
      if (attr && Object.keys(attr).length) {
        for (const name in attr) {
          const value = attr[name];

          if (typeof value === 'boolean') {
            if (value) {
              script.setAttribute(name, '');
            } else {
              script.removeAttribute(name);
            }
          } else {
            script.setAttribute(name, value);
          }
        }
      }
    } else {
      script.text = asset.source;
      resolve();
    }

    root.appendChild(script);
  });
}

function _loadStyle(asset, root) {
  return new Promise((resolve, reject) => {
    if (asset.type === 'stylesheet') {
      const link = document.createElement('link');
      link.onload = resolve;
      link.onerror = reject;
      link.rel = 'stylesheet';
      link.href = asset.source;

      root.appendChild(link);
    } else {
      const style = document.createElement('style');
      style.innerHTML = asset.source;

      root.appendChild(style);
      resolve();
    }
  });
}

function loadStyleAssets(assets, root = document.head) {
  const styleElements = root.querySelectorAll('style');
  const stylesToRender = assets.filter(
    (asset) =>
      asset.source &&
      ((asset.type === 'stylesheet' &&
        !root.querySelector(`link[href='${asset.source}']`)) ||
        (asset.type === 'inlineStyle' &&
          Array.from(styleElements).reduce((acc, cur) => {
            if (cur.innerHTML === asset.source) {
              return false;
            }

            return acc;
          }, true))),
  );

  return Promise.all(stylesToRender.map((asset) => _loadStyle(asset, root)));
}

async function loadScriptAssets(assets, root = document.head) {
  const scriptElements = root.querySelectorAll('script');
  const scriptsToRender = assets.reduce((scripts, asset) => {
    const { source } = asset;
    const _asset = Object.assign({}, asset);

    if (_asset.type !== 'script' && _asset.type !== 'inlineScript') {
      return scripts;
    }

    if (source === Object(source)) {
      if (source.es13 && exported.isES13Supported()) {
        _asset.source = source.es13;
      } else if (source.es11 && exported.isES11Supported()) {
        _asset.source = source.es11;
      } else if (source.es9 && exported.isES9Supported()) {
        _asset.source = source.es9;
      } else {
        _asset.source = null;
      }

      if (!_asset.source) {
        const message = `Asset '${_asset.name}' is missing ES variant and could not be loaded.`;

        if (!_asset.optional) {
          const error = new Error(message);
          error.asset = _asset;

          throw error;
        }

        console.warn(message);
        return scripts;
      }
    }

    if (
      Array.from(scriptElements).reduce((acc, cur) => {
        if (cur.text === _asset.source) {
          return true;
        }

        return acc;
      }, false) ||
      (_asset.test ? exported.test(_asset.test) : false)
    ) {
      return scripts;
    }

    scripts.push(_asset);

    return scripts;
  }, []);

  return Promise.all(scriptsToRender.map((asset) => _loadScript(asset, root)));
}

function loadAssets(assets, root) {
  return Promise.all([
    loadScriptAssets(assets, root),
    loadStyleAssets(assets, root),
  ]);
}

export { loadAssets, loadScriptAssets, loadStyleAssets, exported as testScript };
